import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MarketTag } from "../../components/MarketTag";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import type { MarketType, StrategySpec } from "@strat/format";
import type { WorkbenchOpen } from "@strat/ui";

import { api } from "../../api";
import { PagedList } from "../../components/PagedList";
import { CopySettingsButton, useOpenInWorkbench } from "../../components/CopySettings";
import { StrategyBadges, VerificationPanel } from "../../components/Badges";
import type { LiveBadge } from "../../components/Badges";
import { DeployDialog } from "../../components/DeployDialog";
import { RefChip, SlugEditor } from "../../components/RefChip";
import { EquityChart } from "../../components/EquityChart";
import { SortableTable } from "../../components/SortableTable";
import { SpecSummary } from "../../components/SpecSummary";
import {
  AuthorChip,
  CommentsPanel,
  Disclaimer,
  FollowButton,
  RatingPanel,
  SignInLink,
  Stars,
} from "../../components/social";
import { date, dd, num, pct, timeAgo, tone } from "../../format";
import { useSession } from "../../session";
import { Alert, Card, EmptyState, Field, Stat } from "../../shell/ui";
import { Icon } from "../../components/Icon";

interface StrategyResponse {
  strategy: {
    id: string;
    name: string;
    description: string;
    visibility: "private" | "unlisted" | "public";
    spec: StrategySpec;
    latest_version: number;
    rating_avg: number;
    rating_count: number;
    comment_count: number;
    fork_count: number;
    follower_count: number;
    slug: string;
    published_at: string | null;
    updated_at: string;
    author_username: string;
    author_name: string;
    author_avatar: string;
    author_simulated: boolean;
    author_badge: "verified" | null;
  };
  versions: Array<{
    id: string;
    version: number;
    spec_hash: string;
    note: string;
    run_id: string | null;
    published_at: string;
  }>;
  headline: null | {
    id: string;
    symbol: string;
    timeframe: string;
    market_type: string;
    stats: {
      totalReturnPct: number;
      maxDrawdownPct: number;
      winRatePct: number;
      totalTrades: number;
      profitFactor: number | null;
    };
    params: { initialCapital: number };
    engine_version: string;
    spec_hash: string;
    equity: Array<[number, number]>;
    tradeCount: number;
    verify_count: number;
    verify_ok_count: number;
  };
  forkedFrom: null | {
    id: string;
    name: string;
    slug: string;
    version: number;
    spec_hash: string;
    username: string;
  };
  publicRuns: Array<{
    id: string;
    symbol: string;
    timeframe: string;
    market_type?: string;
    leverage?: number;
    total_return_pct: number;
    max_drawdown_pct: number;
    win_rate_pct: number;
    total_trades: number;
    sharpe: number | null;
    created_at: string;
    title: string | null;
  }>;
  badges: LiveBadge[];
  isOwner: boolean;
}

/** Admin tools on a strategy page: make an official copy, revoke a badge. */
function AdminBadgePanel({ data }: { data: StrategyResponse }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const s = data.strategy;
  const [reason, setReason] = useState("");
  const official = useMutation({
    mutationFn: () =>
      api<{ id: string; ref: string }>(`/hub/admin/strategies/${s.id}/official`, {
        method: "POST",
        body: {},
      }),
    onSuccess: (r) => navigate(`/s/${r.id}`),
  });
  const revoke = useMutation({
    mutationFn: (badgeId: number) =>
      api(`/hub/admin/badges/${badgeId}/revoke`, { method: "POST", body: { reason } }),
    onSuccess: () => {
      setReason("");
      void qc.invalidateQueries({ queryKey: ["hub-strategy", s.id] });
    },
  });
  const isOfficial = s.author_username.toLowerCase() === "strategyhive";
  return (
    <Card title="Admin: badges">
      <div className="stack">
        {data.badges.map((b) => (
          <div key={b.id} className="row">
            <span className="small">
              {b.level} v{b.version}
            </span>
            <input
              placeholder="Why remove it?"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              aria-label="Reason for removing the badge"
            />
            <button
              type="button"
              className="btn btn--sm btn--danger"
              disabled={!reason || revoke.isPending}
              onClick={() => revoke.mutate(b.id)}
            >
              Revoke
            </button>
          </div>
        ))}
        {!isOfficial && data.versions.length > 0 && (
          <button
            type="button"
            className="btn btn--sm"
            disabled={official.isPending}
            onClick={() => official.mutate()}
          >
            ★ Publish an official copy (strategyhive/…)
          </button>
        )}
        {(official.error || revoke.error) && (
          <Alert tone="error">{(official.error ?? revoke.error)!.message}</Alert>
        )}
      </div>
    </Card>
  );
}

function PublishPanel({ data }: { data: StrategyResponse }) {
  const qc = useQueryClient();
  const s = data.strategy;
  const [description, setDescription] = useState(s.description);
  const [note, setNote] = useState("");
  const [visibility, setVisibility] = useState<"public" | "unlisted">(
    s.visibility === "unlisted" ? "unlisted" : "public",
  );
  const publish = useMutation({
    mutationFn: () =>
      api<{ version: number }>(`/hub/strategies/${s.id}/publish`, {
        method: "POST",
        body: { description, note, visibility },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["hub-strategy", s.id] }),
  });
  const unpublish = useMutation({
    mutationFn: () => api(`/hub/strategies/${s.id}/unpublish`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["hub-strategy", s.id] }),
  });
  return (
    <Card title={s.visibility === "private" ? "Publish this strategy" : "Publishing"}>
      <div className="stack">
        <p className="muted small">
          Publishing freezes the current rules as a new version, pinned to their hash, with your latest
          backtest as the headline — drawdown included. Anyone can then re-run it, fork it and rate it.
        </p>
        <Field label="Description">
          <textarea
            rows={3}
            value={description}
            maxLength={4000}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What is the idea, and when does it struggle?"
          />
        </Field>
        <Field label="What changed in this version (optional)">
          <input value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} />
        </Field>
        <Field label="Visibility">
          <select value={visibility} onChange={(e) => setVisibility(e.target.value as "public" | "unlisted")}>
            <option value="public">Public — listed on Explore</option>
            <option value="unlisted">Unlisted — anyone with the link</option>
          </select>
        </Field>
        {publish.error && <Alert tone="error">{publish.error.message}</Alert>}
        {publish.data && <Alert tone="ok">Published as version {publish.data.version}.</Alert>}
        <div className="row">
          <button
            type="button"
            className="btn btn--primary"
            disabled={publish.isPending}
            onClick={() => publish.mutate()}
          >
            {s.visibility === "private" ? "Publish" : "Publish update"}
          </button>
          {s.visibility !== "private" && (
            <button
              type="button"
              className="btn"
              disabled={unpublish.isPending}
              onClick={() => unpublish.mutate()}
            >
              Make private
            </button>
          )}
        </div>
      </div>
    </Card>
  );
}

/**
 * A strategy's public page. Reached by id (`/s/<id>`) or by reference
 * (`/s/alex/rsi-dip:v2`, see RefStrategyPage), which passes the id and the
 * version the reference named.
 */
export function StrategyPage({
  strategyId,
  highlightVersion,
}: { strategyId?: string; highlightVersion?: number | null } = {}) {
  const params = useParams();
  const id = strategyId ?? params.id ?? "";
  const { user } = useSession();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [deploying, setDeploying] = useState(false);
  const openInWorkbench = useOpenInWorkbench();
  const q = useQuery({
    queryKey: ["hub-strategy", id],
    queryFn: () => api<StrategyResponse>(`/hub/strategies/${encodeURIComponent(id)}`),
    retry: false,
  });
  const fork = useMutation({
    mutationFn: () => api<{ id: string }>(`/hub/strategies/${id}/fork`, { method: "POST", body: {} }),
    onSuccess: async (r) => {
      void qc.invalidateQueries({ queryKey: ["hub-strategy", id] });
      // Open the fork on the market its original was tested on — the one the
      // numbers on this page are about — rather than the first in the list.
      const h = q.data?.headline;
      if (!h) return navigate(`/workbench/${r.id}`);
      const { strategy: spec } = await api<{ strategy: StrategySpec }>(`/strategies/${r.id}`);
      navigate(`/workbench/${r.id}`, {
        state: {
          open: {
            spec,
            symbol: h.symbol,
            timeframe: h.timeframe,
            marketType: h.market_type as MarketType,
            initialCapital: h.params.initialCapital,
          } satisfies WorkbenchOpen,
        },
      });
    },
  });

  if (q.isLoading) return null;
  if (!q.data) {
    return (
      <Card>
        <EmptyState icon="◇" title="Strategy not found">
          It may be private, or it no longer exists.
        </EmptyState>
      </Card>
    );
  }
  const { strategy: s, headline: h, versions } = q.data;
  const inv = [["hub-strategy", id]];

  return (
    <div className="stack detail">
      <header className="detail__head">
        <div>
          <p className="muted small">
            Strategy · {s.visibility === "private" ? "private draft" : `v${s.latest_version}`}
            {s.visibility === "unlisted" && " · unlisted"}
          </p>
          <h1 className="page__title">{s.name}</h1>
          <p className="detail__ref">
            <RefChip
              namespace={s.author_username}
              slug={s.slug}
              version={versions[0]?.version ?? null}
              digest={versions[0]?.spec_hash ?? null}
            />
            {q.data.isOwner && <SlugEditor strategyId={s.id} slug={s.slug} name={s.name} />}
          </p>
          <p className="detail__meta">
            <AuthorChip
              username={s.author_username}
              name={s.author_name}
              avatar={s.author_avatar}
              simulated={s.author_simulated}
              badge={s.author_badge}
            />
            <StrategyBadges badges={q.data.badges} latestVersion={versions[0]?.version ?? 0} />
            {s.published_at && <span className="muted small">published {timeAgo(s.published_at)}</span>}
            <Stars value={s.rating_avg} count={s.rating_count} />
          </p>
          {q.data.forkedFrom && (
            <p className="muted small">
              Forked from{" "}
              <RefChip
                namespace={q.data.forkedFrom.username}
                slug={q.data.forkedFrom.slug}
                version={q.data.forkedFrom.version}
                digest={q.data.forkedFrom.spec_hash}
                compact
              />{" "}
              <span className="muted">({q.data.forkedFrom.name})</span>
            </p>
          )}
        </div>
        <div className="row detail__actions">
          {q.data.isOwner ? (
            <Link className="btn" to={`/workbench/${s.id}`} title="Edit this strategy">
              <Icon name="edit" /> Edit in Workbench
            </Link>
          ) : user ? (
            <button
              type="button"
              className="btn"
              title={
                h
                  ? "Open the headline backtest's exact settings in the Workbench"
                  : "Open these rules in the Workbench"
              }
              onClick={() => (h ? navigate(`/workbench?runId=${h.id}`) : openInWorkbench({ spec: s.spec }))}
            >
              <Icon name="workbench" /> Try in Workbench
            </button>
          ) : (
            <Link
              className="btn"
              to={`/signin?next=${encodeURIComponent(h ? `/workbench?runId=${h.id}` : `/s/${s.id}`)}`}
            >
              <Icon name="workbench" /> Try in Workbench
            </Link>
          )}
          {user && !q.data.isOwner && (
            <button
              type="button"
              className="btn"
              disabled={fork.isPending}
              onClick={() => fork.mutate()}
              title="Copy it into your own library and open it in the Workbench"
            >
              <Icon name="fork" /> Fork ({s.fork_count})
            </button>
          )}
          {!user && (
            <SignInLink>
              <Icon name="fork" /> Fork
            </SignInLink>
          )}
          {user && (s.visibility !== "private" || q.data.isOwner) && (
            <button type="button" className="btn btn--primary" onClick={() => setDeploying(true)}>
              <Icon name="bot" /> Run as paper bot
            </button>
          )}
          <FollowButton type="strategy" id={s.id} invalidate={inv} />
          <CopySettingsButton
            settings={
              h
                ? {
                    strategy: s.spec,
                    symbol: h.symbol,
                    timeframe: h.timeframe,
                    marketType: h.market_type,
                    from: h.equity[0]?.[0] ?? null,
                    to: h.equity.at(-1)?.[0] ?? null,
                    initialCapital: h.params.initialCapital,
                  }
                : { strategy: s.spec, symbol: "", timeframe: "", marketType: "spot", initialCapital: 10_000 }
            }
          />
        </div>
      </header>
      {fork.error && <Alert tone="error">{fork.error.message}</Alert>}
      {s.description && <p className="detail__desc">{s.description}</p>}

      <div className="detail__grid">
        <div className="stack">
          {h ? (
            <Card
              title={`Headline backtest · ${h.symbol} ${h.timeframe}`}
              actions={
                <Link className="btn btn--sm" to={`/r/${h.id}`}>
                  Full report & re-run
                </Link>
              }
            >
              <div className="stat-grid stat-grid--pairs">
                <Stat
                  label="Return"
                  value={<span className={tone(h.stats.totalReturnPct)}>{pct(h.stats.totalReturnPct)}</span>}
                />
                <Stat label="Max drawdown" value={dd(h.stats.maxDrawdownPct, 1)} />
                <Stat label="Win rate" value={pct(h.stats.winRatePct, 0, false)} />
                <Stat label="Trades" value={h.stats.totalTrades} />
                <Stat label="Profit factor" value={num(h.stats.profitFactor)} />
                <Stat
                  label="Verified"
                  value={h.verify_count ? `${h.verify_ok_count}/${h.verify_count} ✓` : "not yet"}
                  hint="How many times someone re-ran this and got the same trades"
                />
              </div>
              <EquityChart
                points={h.equity}
                baseline={h.params.initialCapital}
                label="Equity of the headline backtest"
              />
              <p className="muted small mono">
                engine {h.engine_version} · spec {h.spec_hash.slice(0, 12)}…
              </p>
            </Card>
          ) : (
            <Card>
              <EmptyState icon="▦" title="No published backtest yet" />
            </Card>
          )}
          <Card title="The rules">
            <SpecSummary spec={s.spec} />
          </Card>
          {q.data.publicRuns.length > 0 && (
            <Card title="Published runs of this strategy" flush>
              <SortableTable
                exportName={["runs", s.name]}
                rows={q.data.publicRuns}
                rowKey={(r) => r.id}
                onRowClick={(r) => navigate(`/r/${r.id}`)}
                initialSort={{ key: "when", direction: "desc" }}
                columns={[
                  {
                    key: "market",
                    label: "Market",
                    render: (r) => (
                      <>
                        {r.symbol} {r.timeframe} <MarketTag marketType={r.market_type} />
                      </>
                    ),
                    sortValue: (r) => `${r.symbol} ${r.timeframe}`,
                    firstDirection: "asc",
                  },
                  {
                    key: "ret",
                    label: "Return",
                    align: "right",
                    render: (r) => (
                      <span className={tone(r.total_return_pct)}>{pct(r.total_return_pct)}</span>
                    ),
                    sortValue: (r) => r.total_return_pct,
                  },
                  {
                    key: "dd",
                    label: "Max DD",
                    align: "right",
                    render: (r) => dd(r.max_drawdown_pct, 1),
                    sortValue: (r) => r.max_drawdown_pct,
                    firstDirection: "asc",
                  },
                  {
                    key: "win",
                    label: "Win rate",
                    align: "right",
                    render: (r) => pct(r.win_rate_pct, 0, false),
                    sortValue: (r) => r.win_rate_pct,
                  },
                  {
                    key: "trades",
                    label: "Trades",
                    align: "right",
                    render: (r) => r.total_trades,
                    sortValue: (r) => r.total_trades,
                  },
                  {
                    key: "sharpe",
                    label: "Sharpe",
                    align: "right",
                    render: (r) => num(r.sharpe),
                    sortValue: (r) => r.sharpe,
                  },
                  {
                    key: "when",
                    label: "Run",
                    align: "right",
                    render: (r) => timeAgo(r.created_at),
                    sortValue: (r) => new Date(r.created_at).getTime(),
                  },
                ]}
              />
            </Card>
          )}
          <Card>
            <CommentsPanel type="strategy" id={s.id} invalidate={inv} />
          </Card>
        </div>
        <div className="stack">
          {q.data.isOwner && <PublishPanel data={q.data} />}
          {q.data.isOwner && versions.length > 0 && (
            <VerificationPanel strategyId={s.id} headlineRunId={h?.id ?? null} />
          )}
          {user?.role === "admin" && <AdminBadgePanel data={q.data} />}
          <Card title="Ratings & reviews">
            <RatingPanel type="strategy" id={s.id} isOwner={q.data.isOwner} invalidate={inv} />
          </Card>
          <Card title="Versions" flush>
            {versions.length === 0 ? (
              <p className="card__body muted">Not published yet.</p>
            ) : (
              <PagedList
                className="versions"
                items={versions}
                pageSize={10}
                render={(v) => (
                  <li key={v.id} className={v.version === highlightVersion ? "is-highlighted" : undefined}>
                    <strong>v{v.version}</strong> <span className="muted small">{date(v.published_at)}</span>
                    {v.version === highlightVersion && <span className="badge">the version you opened</span>}
                    {v.note && <p className="small">{v.note}</p>}
                    <RefChip
                      namespace={s.author_username}
                      slug={s.slug}
                      version={v.version}
                      digest={v.spec_hash}
                    />
                  </li>
                )}
              />
            )}
          </Card>
          <Card title="Community">
            <p className="small">
              {s.follower_count} following · {s.fork_count} forks · {s.comment_count} comments
            </p>
          </Card>
        </div>
      </div>
      <Disclaimer />
      {deploying && (
        <DeployDialog
          strategyId={s.id}
          strategyName={s.name}
          risk={s.spec.risk}
          direction={s.spec.direction}
          onClose={() => setDeploying(false)}
        />
      )}
    </div>
  );
}
